import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Header from 'components/ui/Header';
import { Users, Shield, CheckCircle, XCircle } from 'lucide-react';
import { API_ENDPOINTS } from '../../utils/config';

const RoleManagement = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalRoles: 0,
    activeUsers: 0,
    inactiveUsers: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Accept': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        // Fetch roles
        const rolesResponse = await axios.get(API_ENDPOINTS.ROLES_LIST, { headers });
        const rolesData = Array.isArray(rolesResponse.data) 
          ? rolesResponse.data 
          : (rolesResponse.data?.results || rolesResponse.data?.data || rolesResponse.data?.roles || []);

        // Fetch user roles
        const userRolesResponse = await axios.get(API_ENDPOINTS.USER_ROLES_LIST, { headers });
        const userRolesData = Array.isArray(userRolesResponse.data)
          ? userRolesResponse.data
          : (userRolesResponse.data?.results || userRolesResponse.data?.data || userRolesResponse.data?.user_roles || []);

        // Calculate stats
        const activeUsers = userRolesData.filter(u => u?.user_detail?.is_active !== false).length;
        const inactiveUsers = userRolesData.filter(u => u?.user_detail?.is_active === false).length;

        setStats({
          totalUsers: userRolesData.length,
          totalRoles: rolesData.length,
          activeUsers: activeUsers,
          inactiveUsers: inactiveUsers
        });
      } catch (error) {
        // Failed to fetch stats handled silently
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statCards = [
    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: Users,
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      borderColor: 'border-blue-200'
    },
    {
      title: 'Total Roles',
      value: stats.totalRoles,
      icon: Shield,
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      borderColor: 'border-purple-200'
    },
    {
      title: 'Active Users',
      value: stats.activeUsers,
      icon: CheckCircle,
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
      borderColor: 'border-green-200'
    },
    {
      title: 'Inactive Users',
      value: stats.inactiveUsers,
      icon: XCircle,
      bgColor: 'bg-red-50',
      iconColor: 'text-red-600',
      borderColor: 'border-red-200'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground">Role Management</h1>
            <p className="text-muted-foreground mt-1">Manage roles and assign roles to users.</p>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {statCards.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className={`p-6 rounded-lg border ${stat.borderColor} ${stat.bgColor} transition-smooth`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">{stat.title}</p>
                      <p className="text-3xl font-bold text-foreground">
                        {isLoading ? '...' : stat.value}
                      </p>
                    </div>
                    <div className={`p-3 rounded-full ${stat.bgColor}`}>
                      <Icon className={`w-6 h-6 ${stat.iconColor}`} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Management Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <a href="/role-management/manage-roles" className="block p-6 rounded-lg border border-border bg-card hover:shadow-elevation-2 transition-smooth">
              <h2 className="text-xl font-semibold mb-2">Manage Roles</h2>
              <p className="text-muted-foreground">Create, edit, and delete roles and permissions.</p>
            </a>
            <a href="/role-management/assign-user-role" className="block p-6 rounded-lg border border-border bg-card hover:shadow-elevation-2 transition-smooth">
              <h2 className="text-xl font-semibold mb-2">Assign User Role</h2>
              <p className="text-muted-foreground">Assign roles to application users.</p>
            </a>
            <a href="/role-management/coordinator-client-assignment" className="block p-6 rounded-lg border border-border bg-card hover:shadow-elevation-2 transition-smooth">
              <h2 className="text-xl font-semibold mb-2">Coordinator Client Assignment</h2>
              <p className="text-muted-foreground">Assign coordinators to clients.</p>
            </a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RoleManagement;


